import JSZip from 'jszip';

// Helper: Get Base Name
export function basename(p) {
    const s = String(p).split(/[\\/]/);
    return s[s.length - 1];
}

// Helper: Guess Mime Type
export function guessMime(filenameLower) {
    if (filenameLower.endsWith('.png')) return 'image/png';
    if (filenameLower.endsWith('.jpg') || filenameLower.endsWith('.jpeg')) return 'image/jpeg';
    if (filenameLower.endsWith('.gif')) return 'image/gif';
    if (filenameLower.endsWith('.svg')) return 'image/svg+xml';
    if (filenameLower.endsWith('.webp')) return 'image/webp';
    if (filenameLower.endsWith('.bmp')) return 'image/bmp';
    return 'application/octet-stream';
}

// Helper: Build Nested Folder Tree
export function buildNestedTree(flatPaths, looseCount) {
    const root = { children: {}, count: 0 };

    for (const [path, count] of Object.entries(flatPaths)) {
        const parts = path.split(' / ');
        let node = root;
        for (const part of parts) {
            if (!node.children[part]) {
                node.children[part] = { children: {}, count: 0 };
            }
            node = node.children[part];
        }
        node.count += count;
    }

    if (looseCount > 0 && Object.keys(root.children).length > 0) {
        root.children['عناصر عامة'] = { children: {}, count: looseCount };
    }

    return root;
}

// Helper: Clean AutoCAD Text strings
export function cleanAutoCADText(rawText) {
    if (rawText === null || rawText === undefined) return "";
    let text = rawText + "";

    // 1. الحفاظ على الرموز الهندسية الأساسية (القطر ø، الدرجة °، الزائد والناقص ±، النسبة %)
    text = text.replace(/%%c/gi, 'ø');
    text = text.replace(/%%d/gi, '°');
    text = text.replace(/%%p/gi, '±');
    text = text.replace(/%%%/g, '%');

    // 2. Handle ASCII codes like %%063 for '?'
    text = text.replace(/%%([0-9]{3})/g, (m, code) => String.fromCharCode(parseInt(code, 10)));

    // 3. Handle Unicode escape sequences \U+XXXX
    text = text.replace(/\\U\+([0-9A-Fa-f]{4,6})/gi, (m, hex) => {
        try {
            return String.fromCodePoint(parseInt(hex, 16));
        } catch (e) { return m; }
    });

    // 4. MText Stacking: \S1/2; or \S1^2; or \S1#2;
    text = text.replace(/\\S([^;]+);/gi, (m, content) => {
        return content.replace(/[\^/#]/g, '/');
    });

    // 5. Handle and remove MText formatting codes (\F, \f, \H, \C, etc.)
    text = text.replace(/\\[A-Fa-f][^;]*;/g, ''); // Font, Color, Height, etc.
    text = text.replace(/\\W[^;]*;/gi, '');      // Width
    text = text.replace(/\\Q[^;]*;/gi, '');      // Oblique
    text = text.replace(/\\T[^;]*;/gi, '');      // Tracking
    text = text.replace(/\\A[^;]*;/gi, '');      // Alignment

    // 6. التعديل السحري 1: تحويل المسافة المخفية إلى مسافة حقيقية لمنع التصاق الكلمات
    text = text.replace(/\\~/g, ' ');

    // 7. التعديل السحري 2: إزالة أكواد التنسيق الفردية مع الحفاظ على الأقواس {} لأنها تعني حروف عربية
    text = text.replace(/\\[PXpxLlOoKk]/g, ''); // Paragraphs, Overline, Underline, Strikeout
    text = text.replace(/%%[uoi]/gi, '');        // Legacy Underline/Overline/Italic toggles

    // ملاحظة هامة: تم حذف سطر إزالة الأقواس [{}] نهائياً للحفاظ على حرفي الدال والجيم في الخطوط القديمة

    // 8. التعديل السحري 4: إزالة أقواس MTEXT {} فقط إذا كانت تحيط بأرقام (مثل {4.71} لتصبح 4.71)
    text = text.replace(/\{([\d\.\s,-]+)\}/g, '$1');

    // إزالة الأقواس إذا كانت تحيط بكامل النص كغلاف
    if (text.startsWith('{') && text.endsWith('}')) {
        text = text.substring(1, text.length - 1);
    }

    return text.trim();
}

// Removed Arabic SHX Decoding Engine as per user request (VERSION 24)

export function colorAlpha(color, alpha) {
    if (!color) return 'rgba(0,0,0,' + alpha + ')';
    if (color.startsWith('#')) {
        const hex = color.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return color;
}

export function simplifyLine(points, tolerance = 0.05) {
    if (points.length <= 2) return points;
    let sqTolerance = tolerance * tolerance;
    function getSqSegDist(p, p1, p2) {
        let x = p1[0], y = p1[1], dx = p2[0]-x, dy = p2[1]-y;
        if (dx !== 0 || dy !== 0) {
            let t = ((p[0]-x)*dx + (p[1]-y)*dy) / (dx*dx + dy*dy);
            if (t > 1) { x = p2[0]; y = p2[1]; }
            else if (t > 0) { x += dx*t; y += dy*t; }
        }
        const dx2 = p[0]-x, dy2 = p[1]-y;
        return dx2*dx2 + dy2*dy2;
    }
    function simplifyDPStep(points, first, last, sqTolerance, simplified) {
        let maxSqDist = sqTolerance, index = -1;
        for (let i = first+1; i < last; i++) {
            const sqDist = getSqSegDist(points[i], points[first], points[last]);
            if (sqDist > maxSqDist) { index = i; maxSqDist = sqDist; }
        }
        if (maxSqDist > sqTolerance) {
            if (index-first > 1) simplifyDPStep(points, first, index, sqTolerance, simplified);
            simplified.push(points[index]);
            if (last-index > 1) simplifyDPStep(points, index, last, sqTolerance, simplified);
        }
    }
    const simplified = [points[0]];
    simplifyDPStep(points, 0, points.length-1, sqTolerance, simplified);
    simplified.push(points[points.length-1]);
    return simplified;
}